/**
 * DashboardRenderer — renders a DashboardConfig in view OR edit mode.
 *
 * Edit mode (B0.17):
 *   - RGL becomes drag + resize enabled (not at sm, where it stacks the widgets)
 *   - Each widget shows a delete button on hover
 *   - A drop (drag or resize stop) is emitted via onLayoutSave, every widget it
 *     moved at once, so the parent can persist it (layout-save.ts)
 *
 * Editor review (2026-10-30): a widget moves from anywhere on it, not from its controls (layout-save.ts DRAG_CANCEL);
 * the click that ends a drag or a resize does not also pick the widget; each control says what it does on hover, and no
 * hint shows while a widget is moved; and a widget that cannot be drawn says so in its own place (ErrorBoundary) instead
 * of blanking the board.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentType, type Ref } from "react";
import { Responsive as ResponsiveGrid } from "react-grid-layout";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Responsive = ResponsiveGrid as unknown as ComponentType<any>;
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { AlertTriangle, GripVertical, Info, RotateCcw, Settings, X } from "lucide-react";
import { cn } from "@/lib/ui/cn";
import { Card } from "@/lib/ui/card";
import { Button } from "@/lib/ui/button";
import { ErrorBoundary } from "@/lib/ui/error-boundary";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/lib/ui/tooltip";
import { WIDGET_REGISTRY } from "./widget-registry";
import {
  canArrange,
  GRID_BREAKPOINTS,
  gridBreakpoint,
  gridCols,
  gridDragConfig,
  gridLayouts,
  gridResizeConfig,
  layoutWrites,
  type LayoutWrite,
} from "./layout-save";
import { useQuery } from "@tanstack/react-query";
import { isLiveApi, listOrgTags } from "@/lib/api";
import { usePlant } from "@/lib/plant/plant-store";
import { bindingProblems, storedTagKindsByIdOf, storedTagKindsOf } from "./data-kinds";
import { useOptionalImportedRows } from "@/lib/imports/store";
import {
  displayLiveTagData,
  useLiveTagData,
  rangeSeedChanged,
  rangeSeedOf,
  windowLabel,
  RANGE_TOKENS,
  type RangeToken,
  type WidgetWindow,
} from "./use-live-tag-data";
import { axisDatumOf, configWithAxisShown } from "./axis-units";
import { isEditable } from "./store";
import { useDisplayUnits } from "@/lib/api/display-units";
import { InvalidBindingCard } from "./InvalidBindingCard";
import { useEquipmentRoles } from "@/lib/api/equipment-roles";
import {
  boundKeysOf,
  decidingNote,
  portEntityOf,
  portNotes,
  portTagOf,
  resolvePorts,
  widgetBands,
  withNamePorts,
  withSentence,
  type PortTags,
} from "./port-binding";
import { useNamePorts } from "./use-name-ports";
import { rangePickerPlacement } from "./range-placement";
import { faultResetKey, rangeHint } from "./editor-hints";
import type { DashboardConfig, WidgetInstance } from "./types";

export interface DashboardRendererProps {
  dashboard: DashboardConfig;
  /** Edit mode — enables drag, resize, delete, palette. */
  editMode?: boolean;
  /** A drop's new layouts: every widget it moved or resized, in one call. */
  onLayoutSave?: (writes: LayoutWrite[]) => void;
  /** Called when the delete button is pressed in edit mode. */
  onDeleteWidget?: (widgetId: string) => void;
  /** Called when a widget is selected (edit mode only). */
  onSelectWidget?: (widgetId: string) => void;
  /** Currently selected widget id (edit mode only). */
  selectedWidgetId?: string | null;
  /**
   * Called when a widget's properties change — title, subtitle, config —
   * via the inline rename or the right-side config panel.
   */
  onWidgetUpdate?: (widgetId: string, patch: Partial<WidgetInstance>) => void;
  className?: string;
}

interface RGLLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The body's class while a widget is moved or resized: index.css hides every hint under it. */
const ARRANGING = "dash-arranging";

function setArranging(on: boolean) {
  if (typeof document === "undefined") return;
  document.body.classList.toggle(ARRANGING, on);
}

/**
 * The corner a widget is resized by, with its hint. The hint is plain CSS on hover (index.css): the grid draws this
 * handle again on every move of a resize, and a tooltip with state there would draw with it.
 */
function resizeHandle(axis: string, ref: Ref<HTMLElement>) {
  return (
    <span
      ref={ref as Ref<HTMLSpanElement>}
      className={`react-resizable-handle react-resizable-handle-${axis} dash-resize-handle`}
    >
      <span className="dash-resize-hint" role="tooltip">
        Drag to resize
      </span>
    </span>
  );
}

const RESIZE_WITH_HINT = { ...gridResizeConfig(true), handleComponent: resizeHandle };

export function DashboardRenderer({
  dashboard,
  editMode = false,
  onLayoutSave,
  onDeleteWidget,
  onSelectWidget,
  selectedWidgetId,
  onWidgetUpdate,
  className,
}: DashboardRendererProps) {
  const cols = dashboard.cols ?? 12;
  const rowHeight = dashboard.rowHeight ?? 80;
  // Pack-shipped dashboards throw on any widget mutation (store.ensureEditable),
  // so a range pick on one persists to local session state only, never config.
  const editable = isEditable(dashboard);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1024);

  // Before the first paint: the grid places its tiles by this width, so a paint at the first guess drew them there. The
  // grid's first commit is still at the guess, and its 200 ms slide starts from there, so a board that is only being
  // read has no slide (dash-viewing, index.css): its tiles stood at the guess until the charts let the slide run.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setWidth(el.offsetWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // lg and md draw the board's own columns, sm stacks the widgets (layout-save.ts).
  const layouts = useMemo(() => gridLayouts(dashboard.widgets), [dashboard.widgets]);
  const breakpoint = gridBreakpoint(width);
  const arrange = editMode && canArrange(breakpoint);
  // react-grid-layout 2 reads dragConfig and resizeConfig: the old isDraggable, isResizable and draggableHandle props
  // were ignored, so every widget could be dragged from anywhere on it, in view mode too.
  const dragConfig = gridDragConfig(arrange);
  const resizeConfig = arrange ? RESIZE_WITH_HINT : gridResizeConfig(false);

  // A drag or a resize starts only past the grid's 3 px threshold, and the click that ends it would also pick the
  // widget (and open its settings). It is marked here and cleared at the next press. While it lasts, no hint shows
  // (index.css, .dash-arranging): the body's class, not React state, so no widget draws again on each move.
  const movedRef = useRef(false);
  const startArranging = () => {
    movedRef.current = true;
    setArranging(true);
  };
  useEffect(() => () => setArranging(false), []);

  // Only a drop is saved. The grid also calls onLayoutChange on mount and on every width or breakpoint change, from its
  // own effects; a save from there went back into the grid and could answer itself until React gave up (#185, a blank
  // board while a widget was dragged).
  const saveDrop = (layout: RGLLayoutItem[]) => {
    setArranging(false);
    if (!editMode || !onLayoutSave) return;
    const writes = layoutWrites(dashboard.widgets, layout, breakpoint);
    if (writes.length > 0) onLayoutSave(writes);
  };
  const select = (widgetId: string) => {
    if (movedRef.current) return;
    onSelectWidget?.(widgetId);
  };

  return (
    <div
      ref={containerRef}
      className={cn("w-full", !editMode && "dash-viewing", className)}
      // A new press or a key starts afresh: the mark of the last drag goes (so a key on a widget's button works), and
      // so does the hint-hiding class, should a drop's mouse-up have been lost outside the window.
      onPointerDownCapture={() => {
        movedRef.current = false;
        setArranging(false);
      }}
      onKeyDownCapture={() => {
        movedRef.current = false;
      }}
    >
      {editMode && !canArrange(breakpoint) && (
        <p className="mb-2 rounded-md border border-dashed border-border px-3 py-2 text-xs text-on-surface-muted">
          The widgets are stacked at this width. Make the window wider to move or resize them.
        </p>
      )}
      <Responsive
        layouts={layouts}
        breakpoints={GRID_BREAKPOINTS}
        cols={gridCols(cols)}
        rowHeight={rowHeight}
        width={width}
        dragConfig={dragConfig}
        resizeConfig={resizeConfig}
        onDragStart={startArranging}
        onResizeStart={startArranging}
        onDragStop={saveDrop}
        onResizeStop={saveDrop}
        margin={[12, 12]}
        containerPadding={[0, 0]}
      >
        {dashboard.widgets.map((widget) => (
          <div key={widget.id} data-widget-id={widget.id}>
            <DashboardWidget
              widget={widget}
              editMode={editMode}
              arrange={arrange}
              isSelected={selectedWidgetId === widget.id}
              onDelete={onDeleteWidget ? () => onDeleteWidget(widget.id) : undefined}
              onSelect={onSelectWidget ? () => select(widget.id) : undefined}
              onRename={
                onWidgetUpdate
                  ? (title) => onWidgetUpdate(widget.id, { title })
                  : undefined
              }
              onConfigChange={
                editable && onWidgetUpdate
                  ? (patch) =>
                      onWidgetUpdate(widget.id, {
                        config: { ...(widget.config ?? {}), ...patch },
                      })
                  : undefined
              }
            />
          </div>
        ))}
      </Responsive>
    </div>
  );
}

function DashboardWidget({
  widget,
  editMode,
  arrange,
  isSelected,
  onDelete,
  onSelect,
  onRename,
  onConfigChange,
}: {
  widget: WidgetInstance;
  editMode: boolean;
  /** Whether the widget can be moved and resized now (edit mode, at a width that is not stacked). */
  arrange: boolean;
  isSelected: boolean;
  onDelete?: () => void;
  onSelect?: () => void;
  onRename?: (title: string) => void;
  /** Persist a partial config patch (range pick). Absent → session-only. */
  onConfigChange?: (patch: Record<string, unknown>) => void;
}) {
  const entry = WIDGET_REGISTRY[widget.type];
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(widget.title ?? "");

  // Keep draft in sync with external title updates (panel changes).
  useEffect(() => {
    if (!renaming) setDraft(widget.title ?? "");
  }, [widget.title, renaming]);

  const commitRename = () => {
    setRenaming(false);
    if (onRename && draft !== widget.title) onRename(draft);
  };

  const name = widget.title || entry?.name || widget.type;
  // What the strip across the top says on hover: what a press, a drag and a double-click do here.
  const stripHint = [
    arrange ? "Drag to move" : undefined,
    onSelect ? "Click to set its data" : undefined,
    onRename ? "Double-click the name to rename" : undefined,
  ]
    .filter(Boolean)
    .join(". ");
  // A widget is drawn again after a fault once what it draws changes (its type, settings or data).
  const resetKey = faultResetKey(widget);

  return (
    <Card
      onClick={editMode && onSelect && !renaming ? onSelect : undefined}
      className={cn(
        "group relative h-full overflow-hidden p-3 transition-colors",
        editMode && "border-dashed border-brand/50 hover:border-brand",
        // The whole card moves the widget (layout-save.ts); without moving (a stacked board) a click still picks it.
        editMode && (arrange ? "dash-drag-handle cursor-grab" : "cursor-pointer"),
        isSelected && "ring-2 ring-brand",
      )}
    >
      {/* "What is this widget?" — a subtle info affordance (appears on hover)
          so users unfamiliar with a widget type can read what it shows.
          Visible in both view + edit mode; sits above the drag strip. */}
      {entry?.description && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={`About the ${entry.name} widget`}
              className="dash-no-drag absolute right-1 top-1 z-30 inline-flex size-6 cursor-help items-center justify-center rounded text-on-surface-muted/60 opacity-50 transition hover:bg-surface/80 hover:text-on-surface hover:opacity-100 focus-visible:opacity-100"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Info className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-[16rem]">
            <p className="text-xs font-semibold text-on-surface">{entry.name}</p>
            <p className="mt-0.5 text-xs text-on-surface-muted">{entry.description}</p>
          </TooltipContent>
        </Tooltip>
      )}
      {editMode && (
        <>
          {/* The strip across the top: a grip and the name. It moves the widget like the rest of the card; its hint
              says what a drag, a click and a double-click do. */}
          {renaming ? (
            <div className="absolute inset-x-0 top-0 z-10 flex h-6 items-center pr-14">
              <input
                autoFocus
                type="text"
                value={draft}
                aria-label="Widget name"
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commitRename}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    commitRename();
                  } else if (e.key === "Escape") {
                    // The page's Escape closes the settings panel: this one only ends the rename.
                    e.stopPropagation();
                    setDraft(widget.title ?? "");
                    setRenaming(false);
                  }
                }}
                placeholder={entry?.name ?? "Widget title"}
                // Suppress react-grid-layout drag while typing
                className="dash-no-drag mx-2 h-5 flex-1 cursor-text rounded bg-surface px-1.5 text-[11px] text-on-surface outline outline-1 outline-brand"
              />
            </div>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="absolute inset-x-0 top-0 z-10 flex h-6 items-center gap-1 bg-gradient-to-b from-brand-muted/40 to-transparent pl-1 pr-14 opacity-70 transition-opacity group-hover:opacity-100"
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (onRename) setRenaming(true);
                  }}
                >
                  {arrange && <GripVertical aria-hidden className="size-3.5 shrink-0 text-on-surface-muted" />}
                  <span className="truncate text-[11px] font-medium text-on-surface-muted group-hover:text-on-surface">
                    {name}
                  </span>
                </div>
              </TooltipTrigger>
              {stripHint && <TooltipContent side="top">{stripHint}.</TooltipContent>}
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "dash-no-drag absolute right-7 top-1 z-20 size-6 bg-surface/80 opacity-0 hover:text-danger focus-visible:opacity-100 group-hover:opacity-100",
                    isSelected && "opacity-100",
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  aria-label="Delete widget"
                >
                  <X className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">Delete widget</TooltipContent>
            </Tooltip>
          )}
        </>
      )}
      <div className={cn("h-full", editMode && "pt-3")}>
        {entry ? (
          <ErrorBoundary
            label={`The ${entry.name} widget "${name}"`}
            resetKey={resetKey}
            fallback={(error, retry) => (
              <WidgetFault name={entry.name} error={error} onRetry={retry} onOpenPanel={editMode ? onSelect : undefined} />
            )}
          >
            <WidgetRenderer
              widget={widget}
              entry={entry}
              onOpenPanel={editMode ? onSelect : undefined}
              editMode={editMode}
              onConfigChange={onConfigChange}
            />
          </ErrorBoundary>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-1 text-center text-xs text-on-surface-muted">
            <span>This board has a widget this app does not know: {widget.type}.</span>
            {editMode && onDelete && <span>Delete it, or open the board in a newer app.</span>}
          </div>
        )}
      </div>
    </Card>
  );
}

/** A widget that threw while it was drawn: said in its own place, so the rest of the board stays up. */
function WidgetFault({
  name,
  error,
  onRetry,
  onOpenPanel,
}: {
  name: string;
  error: Error;
  onRetry: () => void;
  onOpenPanel?: () => void;
}) {
  return (
    <div role="alert" className="flex h-full flex-col items-center justify-center gap-1.5 p-2 text-center">
      <AlertTriangle aria-hidden className="size-4 text-warning" />
      <p className="text-xs font-medium text-on-surface">This {name} widget could not be drawn.</p>
      {error.message && (
        <p className="line-clamp-2 max-w-full break-words text-[11px] text-on-surface-muted">{error.message}</p>
      )}
      <div className="flex flex-wrap justify-center gap-1.5">
        <Button
          size="sm"
          variant="outline"
          className="h-6 px-2 text-[11px]"
          onClick={(e) => {
            e.stopPropagation();
            onRetry();
          }}
        >
          <RotateCcw className="mr-1 size-3" />
          Try again
        </Button>
        {onOpenPanel && (
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[11px]"
            onClick={(e) => {
              e.stopPropagation();
              onOpenPanel();
            }}
          >
            <Settings className="mr-1 size-3" />
            Check its settings
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Render-time guard: validates each required slot's bound keys against
 * its declared `dataKinds`. If anything's off, draws InvalidBindingCard
 * instead of letting Recharts NaN.
 */
function WidgetRenderer({
  widget,
  entry,
  onOpenPanel,
  editMode,
  onConfigChange,
}: {
  widget: WidgetInstance;
  entry: (typeof WIDGET_REGISTRY)[keyof typeof WIDGET_REGISTRY];
  onOpenPanel?: () => void;
  editMode?: boolean;
  onConfigChange?: (patch: Record<string, unknown>) => void;
}) {
  const plant = usePlant();
  const imports = useOptionalImportedRows();

  // Per-widget time window. Seeded from the saved `config.range`, held
  // locally so the picker responds instantly (and still works on read-only
  // pack dashboards, where a config write would throw). When the dashboard is
  // editable the pick is ALSO persisted via onConfigChange, so local state and
  // config stay in lockstep. A different widget remounts this component
  // (keyed by id), re-seeding from its own config; and the same widget
  // re-seeds when its saved range or window changes (#1675), say a save read
  // back or an Exit that reverted. The seed is compared by value, so a config
  // that is only a new object keeps a pick the config does not hold.
  const seed = useMemo(() => rangeSeedOf(widget.config), [widget.config]);
  const [seeded, setSeeded] = useState(seed);
  const [range, setRange] = useState<RangeToken | undefined>(seed.range);
  // The board's pinned window of days (`config.window`, the data's own period) wins over the lookback. A lookback chip
  // looks at another period on this screen only: it never unpins the board for everyone, and the pinned chip brings the
  // window back (#994 review, LOW-5). A board with no pinned window saves its lookback, as before.
  const savedWindow = seed.window;
  const [dataWindow, setDataWindow] = useState<WidgetWindow | undefined>(savedWindow);
  if (rangeSeedChanged(seeded, seed)) {
    setSeeded(seed);
    setRange(seed.range);
    setDataWindow(seed.window);
  }
  const pickRange = (next: RangeToken | undefined) => {
    setRange(next);
    setDataWindow(undefined);
    if (!savedWindow) onConfigChange?.({ range: next });
  };

  // A port binding reads its ports through the machine's roles (B6, port-binding.ts): each port's own primary tag, by
  // id, or its one series when several sources feed it (one series per port, S3). A board card's binding (byEntity)
  // reads nothing else, and nothing until the roles are read; an older binding whose alias names one machine falls back
  // to names for any key the roles do not list.
  const portEntity = useMemo(() => portEntityOf(widget.dataSource, plant.nodes), [widget.dataSource, plant.nodes]);
  const machineRoles = useEquipmentRoles(portEntity?.entityId);
  const ports = useMemo<PortTags | undefined>(() => {
    if (!portEntity) return undefined;
    if (!machineRoles.data) return portEntity.exclusive ? { byKey: new Map(), exclusive: true } : undefined;
    return {
      byKey: resolvePorts(widget.dataSource, machineRoles.data.roles, portEntity.exclusive, portEntity.entityId),
      exclusive: portEntity.exclusive,
    };
  }, [portEntity, machineRoles.data, widget.dataSource]);
  // The org's tags: their stored kinds for the binding check (a tag binding is judged by what its tag stores, never by
  // its name alone: bindingKind), and the names two tags share. The same query useLiveTagData reads. Live and still
  // loading: undefined, judged later.
  const orgTags = useQuery({ queryKey: ["org-tags"], queryFn: listOrgTags, enabled: isLiveApi, staleTime: 30_000 });
  // A key read by a name that the sources of one machine port share reads that port's series (S3, use-name-ports.ts):
  // only a tag or port binding reads keys by name.
  const boundKeys = useMemo(() => boundKeysOf(widget.dataSource), [widget.dataSource]);
  const nameKeys = useMemo(
    () => (widget.dataSource?.kind === "tag" || widget.dataSource?.kind === "port" ? boundKeys : []),
    [widget.dataSource, boundKeys],
  );
  const { resolutions: namePorts, loading: namePortsLoading } = useNamePorts(nameKeys, orgTags.data?.items, ports);
  const readPorts = useMemo(() => withNamePorts(ports, namePorts), [ports, namePorts]);

  // Live mode: resolve this widget's tag bindings to real readings (latest +
  // recent series) over the selected window, polled. Empty in stub mode / for
  // unresolved bindings → resolvers fall through to imported/fixture data.
  const { byKey: storedTagData, span: liveSpan, loading: readingsLoading } = useLiveTagData(
    widget.dataSource,
    range,
    dataWindow,
    readPorts,
  );
  // The readings are on their way, or what they are read through is: the plant a port's machine is found in, the
  // machine's roles, the port a shared name stands for. Until then no read has started: an empty widget says nothing.
  const portsLoading =
    nameKeys.length > 0 &&
    (machineRoles.isLoading ||
      namePortsLoading ||
      (plant.loading && widget.dataSource?.kind === "port" && !widget.dataSource.byEntity));
  const loading = readingsLoading || portsLoading;
  const storedTagKinds = useMemo(
    () =>
      storedTagKindsOf({
        live: isLiveApi,
        items: orgTags.data ? (orgTags.data.items ?? []) : undefined,
        isError: orgTags.isError,
      }),
    [orgTags.data, orgTags.isError],
  );
  // The same tags by id, which a machine's port is judged by (two tags may share a port tag's name).
  const storedKindsById = useMemo(
    () =>
      storedTagKindsByIdOf({
        live: isLiveApi,
        items: orgTags.data ? (orgTags.data.items ?? []) : undefined,
        isError: orgTags.isError,
      }),
    [orgTags.data, orgTags.isError],
  );
  // The readings as the org reads them: each tag's latest value, its series and its unit move together into the org's
  // units, so no chart shows one unit's data on another's axis (displayLiveTagData). The same object for a metric org.
  const { shown: unitPrefs } = useDisplayUnits();
  const liveTagData = useMemo(() => displayLiveTagData(storedTagData, unitPrefs), [storedTagData, unitPrefs]);
  // A fixed axis is drawn in the unit its data is shown in, whatever unit it was saved in (#1190 review MEDIUM 2).
  const shownConfig = useMemo(
    () => configWithAxisShown(widget.config, axisDatumOf(widget.dataSource, liveTagData)),
    [widget.config, widget.dataSource, liveTagData],
  );

  // B2.3a-bis: pre-fetch the rows for the widget's bound imported dataset
  // (if any) so the registry entry can resolve against them. Lookup is
  // O(1) once the rows are cached in the provider; first miss hits
  // localStorage.
  const importedDatasetId = widget.dataSource?.importedDatasetId;
  const importedRows =
    importedDatasetId && imports
      ? imports.byDataset(importedDatasetId)
      : undefined;

  // A board card's machine binding is judged by its ports' own tags once the roles are read, and not before.
  const portTag = portEntity?.exclusive ? (machineRoles.data ? portTagOf(ports) : () => null) : undefined;
  const problems = bindingProblems(entry.accepts, widget.dataSource, plant.nodes, storedTagKinds, portTag, storedKindsById);
  // The band each key draws (the machine's yardstick from its roles, or the card's own ends), in the unit it is shown
  // in; and what the widget says of a port it cannot draw (two feeds, not bound) or of roles that could not be read.
  const bands = useMemo(
    () => widgetBands(widget.config, boundKeys, readPorts, liveTagData, unitPrefs),
    [widget.config, boundKeys, readPorts, liveTagData, unitPrefs],
  );
  const portNote = portEntity?.exclusive && machineRoles.isError
    ? "The machine's roles could not be read, so its ports are not drawn."
    : portNotes(ports);
  // A port read as its series that is not decided yet draws its older part from its first choice alone (S3): say so.
  const subtitle = withSentence(withSentence(widget.subtitle, portNote), decidingNote(storedTagData));

  if (problems.length > 0) {
    return (
      <InvalidBindingCard
        widgetName={entry.name}
        problems={problems}
        onOpenPanel={onOpenPanel}
      />
    );
  }

  const rendered = (
    loading && entry.loading
      ? entry.loading({ title: widget.title, subtitle, config: shownConfig, keys: nameKeys.length })
      : entry.render({
          title: widget.title,
          subtitle,
          bands,
          config: shownConfig,
          dataSource: widget.dataSource,
          importedRows,
          liveTagData,
          liveSpan,
          range,
          dataWindow,
        })
  ) as ReturnType<typeof entry.render>;

  // A per-widget window picker for the widgets whose live view is time-scoped
  // (charts + the table's history mode). In edit mode it keeps its place in the
  // widget's header row, under the drag strip (#1674), so it never collides with
  // the strip / delete / info chrome across the top edge.
  const placement = rangePickerPlacement({
    live: isLiveApi,
    editMode: !!editMode,
    type: widget.type,
    dataSource: widget.dataSource,
  });
  if (placement === "none") return rendered;

  const picker = (
    <RangeSelector
      value={range}
      pinned={savedWindow}
      pinnedActive={dataWindow !== undefined}
      onRepin={() => setDataWindow(savedWindow)}
      onChange={pickRange}
      allowLatest={widget.type === "table"}
      placement={placement}
    />
  );

  if (placement === "row") {
    return (
      <div className="flex h-full flex-col">
        <div className="mt-1 flex shrink-0 justify-end">{picker}</div>
        <div className="relative min-h-0 flex-1">{rendered}</div>
      </div>
    );
  }

  return (
    <div className="relative h-full">
      {picker}
      {rendered}
    </div>
  );
}

/**
 * Compact segmented window picker rendered on a live, time-scoped widget.
 * `allowLatest` (table only) prepends a "Latest" chip that clears the range
 * (→ the latest-per-tag snapshot); every other chip pins a lookback window.
 * When nothing is pinned the effective window is the wide DEFAULT_RANGE ("1y"),
 * so that chip reads as active for non-table widgets.
 */
export function RangeSelector({
  value,
  pinned,
  pinnedActive = pinned !== undefined,
  onRepin,
  onChange,
  allowLatest,
  placement = "overlay",
}: {
  value: RangeToken | undefined;
  /** The board's pinned window of days: shown first, and a click on it brings it back after a lookback was picked. */
  pinned?: WidgetWindow;
  /** Whether the pinned window is what the widget shows now. */
  pinnedActive?: boolean;
  onRepin?: () => void;
  onChange: (next: RangeToken | undefined) => void;
  allowLatest: boolean;
  /** "overlay" floats over the widget's corner; "row" takes its place in the flow of the widget's header row. */
  placement?: "overlay" | "row";
}) {
  const options: Array<{ token: RangeToken | undefined; label: string }> = [
    ...(allowLatest ? [{ token: undefined, label: "Latest" }] : []),
    ...RANGE_TOKENS.map((t) => ({
      token: t as RangeToken | undefined,
      label: t === "all" ? "All" : t,
    })),
  ];
  // Non-table widgets always have an effective window; surface the default.
  const active: RangeToken | undefined = value ?? (allowLatest ? undefined : "1y");
  const tokenId = (t: RangeToken | undefined) => t ?? "__latest__";
  // Its own provider: the picker is also drawn outside the app's layout (tests, other pages).
  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={cn(
          "dash-no-drag flex overflow-hidden rounded-md border border-border bg-surface/90 shadow-sm backdrop-blur",
          placement === "overlay" && "absolute right-8 top-1 z-20",
        )}
        role="group"
        aria-label="Time range"
      >
        {pinned && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-pressed={pinnedActive}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onRepin?.();
                }}
                className={cn(
                  "px-1.5 py-0.5 text-[10px] font-medium transition-colors",
                  pinnedActive
                    ? "bg-brand text-brand-foreground"
                    : "text-on-surface-muted hover:bg-surface-muted hover:text-on-surface",
                )}
              >
                {windowLabel(pinned)}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              The board's window: {pinned.from} to {pinned.to}
            </TooltipContent>
          </Tooltip>
        )}
        {options.map((o) => {
          const isActive = !pinnedActive && tokenId(o.token) === tokenId(active);
          return (
            <Tooltip key={o.label}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-pressed={isActive}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(o.token);
                  }}
                  className={cn(
                    "px-1.5 py-0.5 text-[10px] font-medium transition-colors",
                    isActive
                      ? "bg-brand text-brand-foreground"
                      : "text-on-surface-muted hover:bg-surface-muted hover:text-on-surface",
                  )}
                >
                  {o.label}
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{rangeHint(o.token)}</TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
